import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  NcsColorPicker,
  collectUsedNcsColorsFromPlan,
} from '../../../../features/ncsColor';
import { editorStore } from '../../../../store/editorStore';
import {
  getFillColor,
  getFillOpacity,
  normalizeFillPattern,
  numberToHexColor,
  supportsPatternFill,
  updateFillPatternForTargets,
} from '../../../../utils/fillSelection';
import FillPatternSelector from '../../FillPatternSelector';
import ColorField from '../components/ColorField';
import OpacityField from '../components/OpacityField';
import PanelSection from '../components/PanelSection';
import type { PropertyPanelController } from '../types';

type FillSectionProps = Pick<PropertyPanelController, 'fillPropsOpen' | 'setFillPropsOpen' | 'handleFillColorChange' | 'flushPendingFillColorUpdate' | 'handleFillOpacityChange' | 'flushPendingFillOpacityUpdate' | 'handleFillOpacityRestore'> & {
  grouped: PropertyPanelController['grouped'];
};

/**
 * Fill color / opacity / pattern for the current selection.
 * Observes the store so pinned “used in plan” NCS swatches stay in sync.
 */
const FillSection: React.FC<FillSectionProps> = observer(({
  grouped,
  fillPropsOpen,
  setFillPropsOpen,
  handleFillColorChange,
  flushPendingFillColorUpdate,
  handleFillOpacityChange,
  flushPendingFillOpacityUpdate,
  handleFillOpacityRestore,
}) => {
  const { t } = useTranslation();
  const mixed = t('common.mixed');
  const {
    fillSectionObjects,
    firstFillSectionObject,
    sharedFillColor,
    fillOpacityObjects,
    firstFillOpacityObject,
    sharedFillOpacity,
    patternFillObjects,
    sharedPattern,
    hasMixedPatternSelection,
  } = grouped;

  const pinnedNcsColors = collectUsedNcsColorsFromPlan();

  if (editorStore.selections.length === 0 || fillSectionObjects.length === 0) {
    return null;
  }

  const hasMixedFillColor =
    fillSectionObjects.length > 1 && sharedFillColor === undefined;
  const currentFillHex = firstFillSectionObject
    ? (sharedFillColor ?? numberToHexColor(getFillColor(firstFillSectionObject)))
    : '#ffffff';

  return (
    <PanelSection
      title={t('propertyPanel.fill')}
      collapsible
      open={fillPropsOpen}
      onToggle={() => setFillPropsOpen(!fillPropsOpen)}
      maxHeightClassName="max-h-[820px]"
    >
      <div className="space-y-0.5 px-3 pb-3 pt-0">
        {firstFillSectionObject && (
          <div key={`fill-section-${firstFillSectionObject.id}`}>
            <ColorField
              value={currentFillHex}
              mixed={hasMixedFillColor}
              mixedLabel={mixed}
              onChange={(value) => handleFillColorChange(fillSectionObjects, value)}
              onBlur={flushPendingFillColorUpdate}
            />
            <NcsColorPicker
              value={hasMixedFillColor ? undefined : currentFillHex}
              pinnedColors={pinnedNcsColors}
              onSelect={(hex) => {
                handleFillColorChange(fillSectionObjects, hex);
                flushPendingFillColorUpdate();
              }}
              onClear={() => {
                handleFillColorChange(fillSectionObjects, '#ffffff');
                flushPendingFillColorUpdate();
              }}
            />
            {firstFillOpacityObject && (
              <OpacityField
                value={sharedFillOpacity ?? getFillOpacity(firstFillOpacityObject)}
                mixed={fillOpacityObjects.length > 1 && sharedFillOpacity === undefined}
                mixedLabel={mixed}
                onChange={(value) => handleFillOpacityChange(fillOpacityObjects, value)}
                onCommit={flushPendingFillOpacityUpdate}
                onRestore={() => handleFillOpacityRestore(fillOpacityObjects)}
              />
            )}
            {editorStore.selections.length === 1 &&
              supportsPatternFill(firstFillSectionObject) && (
                <FillPatternSelector
                  object={firstFillSectionObject}
                  currentPattern={normalizeFillPattern(
                    firstFillSectionObject.fillPattern,
                  )}
                  onUpdate={(_object, _property, value) =>
                    updateFillPatternForTargets(firstFillSectionObject, value)
                  }
                />
              )}
          </div>
        )}
        {editorStore.selections.length > 1 && patternFillObjects.length > 0 && (
          <FillPatternSelector
            object={patternFillObjects[0]}
            currentPattern={sharedPattern}
            isMixed={hasMixedPatternSelection}
            onUpdate={(_object, _property, value) =>
              updateFillPatternForTargets(patternFillObjects, value)
            }
          />
        )}
      </div>
    </PanelSection>
  );
});

export default FillSection;
